import {
  applySnapshot,
  flow,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import { GetAllowedDomainsRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getAllowedDomainsRPC";
import { getAPIClient } from "../../networking/APIClient";
import { LeoRPCResult } from "@surya-digital/leo-ts-runtime";
import {
  createAllowedDomainModel,
  AllowedDomainModel,
} from "../models/AllowedDomainModel";
import { createBoundedNameModel } from "../../models/BoundedNameModel";
import { useGetAllowedDomainsRPCClientImpl } from "../rpcs/RPC";

export const ALLOWED_DOMAINS_PER_PAGE = 10;

const getInitialState = (): SnapshotIn<typeof AllowedDomainsStore> => ({
  allowedDomains: [],
  totalItems: 0,
  currentPageIndex: 1,
  loadingAllowedDomains: true,
});

export const AllowedDomainsStore = types
  .model("AllowedDomainsStore", {
    allowedDomains: types.array(AllowedDomainModel),
    totalItems: types.integer,
    currentPageIndex: types.integer,
    loadingAllowedDomains: types.boolean,
  })
  .actions((store) => ({
    getAllowedDomains: flow(function* (
      pageIndex = 1,
      loadingOptionsChange = false,
    ) {
      if (!loadingOptionsChange) store.loadingAllowedDomains = true;

      try {
        const apiClient = getAPIClient(store);
        const request = new GetAllowedDomainsRPC.Request(
          pageIndex - 1,
          ALLOWED_DOMAINS_PER_PAGE,
        );

        const result: LeoRPCResult<
          GetAllowedDomainsRPC.Response,
          GetAllowedDomainsRPC.Errors.Errors
        > = yield useGetAllowedDomainsRPCClientImpl(apiClient).execute(request);

        if ("response" in result) {
          store.currentPageIndex = pageIndex;
          store.allowedDomains.replace(
            result.response.domains.map((domain) =>
              createAllowedDomainModel({
                id: domain.domainId.uuid,
                domainName: createBoundedNameModel({
                  name: domain.domainName.name,
                }),
                domain: createBoundedNameModel({
                  name: domain.domain.name,
                }),
              }),
            ),
          );
          store.totalItems = result.response.totalItems;
        }
      } finally {
        if (!loadingOptionsChange) store.loadingAllowedDomains = false;
      }
    }),
    refreshCurrentPage: flow(function* () {
      yield (
        store as typeof store & {
          getAllowedDomains: (
            pageIndex?: number,
            loadingOptionsChange?: boolean,
          ) => Promise<void>;
        }
      ).getAllowedDomains(store.currentPageIndex);
    }),
    refreshCurrentPageAfterDeletion: flow(function* () {
      yield (
        store as typeof store & {
          getAllowedDomains: (
            pageIndex?: number,
            loadingOptionsChange?: boolean,
          ) => Promise<void>;
        }
      ).getAllowedDomains(store.currentPageIndex);

      if (
        store.currentPageIndex > 1 &&
        !store.allowedDomains.length &&
        store.totalItems > 0
      ) {
        yield (
          store as typeof store & {
            getAllowedDomains: (
              pageIndex?: number,
              loadingOptionsChange?: boolean,
            ) => Promise<void>;
          }
        ).getAllowedDomains(store.currentPageIndex - 1);
      }
    }),
    resetStore: (): void => applySnapshot(store, getInitialState()),
  }));

export const createAllowedDomainsStore = (): Instance<
  typeof AllowedDomainsStore
> => AllowedDomainsStore.create(getInitialState());
